import type {
	FieldKey,
	ListInvestmentsDTO,
	UserPortfolioColumnPreferencePreferencesTypeEnum,
} from "@eu.mdotm.api/sphere";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { sharingWithColumn } from "$root/functional-areas/acl/table-columns";
import UserShareIcon from "$root/functional-areas/acl/UserShareIcon";
import { aclToUsersListEntries } from "$root/functional-areas/acl/UsersList";
import type { CompareDataItem } from "$root/functional-areas/compare-portfolio/CompareOverlay";
import type { MinimumCompareProps } from "$root/functional-areas/compare-portfolio/MinimumCompareProps";
import { useUserValue } from "$root/functional-areas/user";
import { useLocaleFormatters } from "$root/localization/hooks";
import { PortfolioDetailsTabs } from "$root/pages/PortfolioDetails/portfolio-details-tabs";
import { builtInCaseInsensitiveSortFor } from "$root/utils/collections";
import {
	type TableColumn,
	ActionText,
	AutoTooltip,
	Icon,
	Row,
	TableDataCell,
	Text,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { t } from "i18next";
import { useMemo } from "react";
import { statusIconMap } from "../statusIconMap";
import ReactQueryWrapper from "$root/components/ReactQueryWrapper";
import PortfolioExposureSummary from "$root/functional-areas/compare-portfolio/PortfolioExposureSummary";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { commonColumns } from "$root/ui-lib/common-table-columns";
import { AutoSyncStatus } from "../../../functional-areas/portfolio/AutoSyncStatus";
import type { ColumnMetadata } from "$root/ui-lib/table/extra/ColumnChooser";
import { ReviewEntity } from "$root/pages/ReviewInstruments.const";

export function usePortfolioColumn({
	comparablePortfolios,
	toggleComparePortfolio,
	columnsPreferences,
}: {
	comparablePortfolios?: CompareDataItem[];
	toggleComparePortfolio?: (portfolio: MinimumCompareProps, enhanced?: boolean) => void;
	columnsPreferences?: ColumnMetadata<UserPortfolioColumnPreferencePreferencesTypeEnum>[];
}): TableColumn<ListInvestmentsDTO, FieldKey>[] {
	const allColumns = usePortfolioColumns();

	const columns = useMemo(
		() =>
			columnsPreferences
				? columnsPreferences.flatMap((columnMeta) =>
						columnMeta.id && columnMeta.id in allColumns
							? [
									{
										...allColumns[columnMeta.id as keyof typeof allColumns]({
											comparablePortfolios,
											toggleComparePortfolio,
										}),
										hidden: !columnMeta.checked,
									},
								]
							: [],
					)
				: [],
		[columnsPreferences, allColumns, comparablePortfolios, toggleComparePortfolio],
	);

	return columns;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function usePortfolioColumns() {
	const formatters = useLocaleFormatters();
	const { formatDate } = formatters;
	const user = useUserValue();

	return useMemo(
		() =>
			({
				NAME: (params?: {
					comparablePortfolios?: CompareDataItem[];
					toggleComparePortfolio?: (portfolio: MinimumCompareProps, enhanced?: boolean) => void;
				}) => ({
					header: t(`TABLE.HEADERS.NAME`),
					content: (row, props) => {
						const { uuid, name, richAcl, status, action } = row;

						const isManualCreation = status === "DRAFT" && action === "UPLOAD";
						const isPipelineCreation = status === "DRAFT" && action === "CREATION";

						const selected = params?.comparablePortfolios?.find((x) => x.uuid === uuid && !x.enhanced) !== undefined;

						const selectedEnhanced =
							params?.comparablePortfolios?.find((x) => x.uuid === uuid && x.enhanced) !== undefined;

						return (
							<AutoTooltip
								severity="info"
								position="right"
								disabled={
									status === "ERROR" ||
									status === "CALCULATING" ||
									status === "REVIEW" ||
									status === "DRAFT" ||
									status === "RETRIEVING_DATA"
								}
								openDelay={700}
								trigger={({ innerRef }) => (
									<div style={props.style} className={overrideClassName(props.className, "items-center flex")}>
										<ActionText
											className="line-clamp-2"
											color={themeCSSVars.palette_N900}
											href={
												isManualCreation
													? typedUrlForRoute("Portfolios/ManualPortfolioCreation", { uuid })
													: isPipelineCreation
														? typedUrlForRoute("Portfolios/Draft", { portfolioUid: uuid! })
														: status === "REVIEW"
															? typedUrlForRoute("ReviewInstruments", { uuid: uuid!, entity: ReviewEntity.INVESTMENT })
															: typedUrlForRoute("PortfolioDetails", { portfolioUid: uuid! })
											}
											title={name}
											innerRef={innerRef}
											data-qualifier="PortfolioStudio/PortfolioList/Table/Column(Name)"
										>
											{name}
										</ActionText>
										<UserShareIcon
											entity="INVESTMENT"
											userId={user.id}
											permission={richAcl?.currentUserPermissions}
											color={themeCSSVars.palette_N300}
											acl={richAcl?.acl}
										/>
									</div>
								)}
							>
								<TooltipContent>
									<div className="w-[280px] overflow-hidden space-y-2">
										<ReactQueryWrapper {...portfolioRepository.comparisonDetails.getQueryConfig(row.uuid!)}>
											{({ macroAssetClassExposure, macroAssetClassExposureEnhanced, lastActionNote }) => (
												<>
													<PortfolioExposureSummary
														compared={selected}
														subtitle="current"
														composition={macroAssetClassExposure ?? []}
														onCompare={() => {
															params?.toggleComparePortfolio?.({
																action: row.action,
																name: row.name,
																uuid: row.uuid,
																lastActionNote,
																nofInstruments: row.nofInstruments,
																macroAssetClassExposure,
																macroAssetClassExposureEnhanced,
															});
														}}
														title={name ?? "-"}
													/>
													{macroAssetClassExposureEnhanced && row.status === "PROPOSAL_READY" && (
														<PortfolioExposureSummary
															compared={selectedEnhanced}
															subtitle="proposal"
															subtitleClassName={`!text-[color:var(--palette-S400)]`}
															composition={macroAssetClassExposureEnhanced ?? []}
															onCompare={(e) => {
																e.stopPropagation();
																params?.toggleComparePortfolio?.(
																	{
																		action: row.action,
																		name: row.name,
																		uuid: row.uuid,
																		lastActionNote,
																		nofInstruments: row.nofInstruments,
																		macroAssetClassExposure,
																		macroAssetClassExposureEnhanced,
																	},
																	true,
																);
															}}
															action={action}
														/>
													)}
												</>
											)}
										</ReactQueryWrapper>
									</div>
								</TooltipContent>
							</AutoTooltip>
						);
					},
					sortFn: builtInCaseInsensitiveSortFor("name"),
					name: "NAME",
					width: 320,
				}),

				SHARING_VIEWS: (_params?: unknown) => ({
					...sharingWithColumn({
						users: ({ richAcl }) => aclToUsersListEntries("portfolio", richAcl?.acl ?? []),
					}),
					header: t(`TABLE.HEADERS.SHARING_VIEWS`),
					name: "SHARING_VIEWS",
				}),

				INCEPTION_DATE: (_params?: unknown) => ({
					...commonColumns.date("creationTime"),
					header: t(`TABLE.HEADERS.INCEPTION_DATE`),
					name: "INCEPTION_DATE",
					minWidth: 128,
				}),

				CURRENT_ACTION: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.CURRENT_ACTION`),
					content: ({ action }) => {
						const translatedAction = action ? t(`PORTFOLIO_ACTION_STATUS.${action}`) : "-";
						return translatedAction;
					},
					sortFn: builtInCaseInsensitiveSortFor("action"),
					name: "CURRENT_ACTION",
					minWidth: 92,
				}),

				STATUS: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.STATUS`),
					content: ({ status }, cellProps) => {
						const sanitizedStatus = status ? statusIconMap[status] : "-";
						if (sanitizedStatus === "-") {
							return (
								<TableDataCell {...cellProps} data-qualifier="PortfolioStudio/PortfolioList/Table/Column(Status)">
									{sanitizedStatus}
								</TableDataCell>
							);
						}
						return (
							<Row
								{...cellProps}
								title={sanitizedStatus.title}
								alignItems="center"
								data-qualifier="PortfolioStudio/PortfolioList/Table/Column(Status)"
							>
								<Icon
									className={{
										"mr-1": true,
										// Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
										"rotate-180": status === "ERROR",
									}}
									icon={sanitizedStatus.icon}
									color={themeCSSVars.palette_N800} // changed
									size={sanitizedStatus.size}
								/>
								<Text type="Body/M/Book" className="line-clamp-2">
									{sanitizedStatus.title}
								</Text>
							</Row>
						);
					},
					sortFn: builtInCaseInsensitiveSortFor("status"),
					name: "STATUS",
					minWidth: 128,
				}),

				LAST_STATUS_UPDATE: (_params?: unknown) => ({
					...commonColumns.date("modificationTime"),
					header: t(`TABLE.HEADERS.LAST_STATUS_UPDATE`),
					name: "LAST_STATUS_UPDATE",
					minWidth: 92,
				}),

				AUTO_SYNC: () => ({
					header: t(`TABLE.HEADERS.AUTO_SYNC`),
					content: (row, props) => {
						if ((row.importedExternally && row.syncDate) || row.importedPerformances?.lastImportDate) {
							return (
								<TableDataCell style={props.style} className={props.className} onClick={props.onClick}>
									<AutoSyncStatus
										variant="compat"
										syncDate={row.syncDate}
										lastMetricImportDate={row.importedPerformances?.lastImportDate}
									/>
								</TableDataCell>
							);
						}

						return null;
					},
					minWidth: 128,
					sortFn: builtInSortFnFor("importedExternally"),
					name: "AUTO_SYNC",
				}),

				MARKET_VIEW_NAME: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.MARKET_VIEW_NAME`),
					content: (row, props) => {
						if (!row.marketView) {
							return null;
						}

						const { scenarioName, scenarioIdentifier, forecastDates } = row.marketView;
						return (
							<TableDataCell {...props}>
								<ActionText
									className="line-clamp-2"
									href={
										scenarioIdentifier === "CUSTOM_PORTFOLIO_MARKET_VIEW"
											? `${typedUrlForRoute("PortfolioDetails", {
													// redirect to portfolio settings
													portfolioUid: row?.uuid ?? "",
												})}?tab=${PortfolioDetailsTabs.PORTFOLIO_STUDIO_SETTINGS}#marketView`
											: scenarioIdentifier
												? typedUrlForRoute("MarketViewWorkSpace/View", { uuid: scenarioIdentifier })
												: ""
									}
									title={!scenarioName ? `Market view ${formatDate(forecastDates?.from)}` : scenarioName}
								>
									{!scenarioName ? `Market view ${formatDate(forecastDates?.from)}` : scenarioName}
								</ActionText>
							</TableDataCell>
						);
					},
					minWidth: 192,
					name: "MARKET_VIEW_NAME",
					sortFn: builtInSortFnFor("marketView"),
				}),

				CURRENCY_NAME: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.CURRENCY_NAME`),
					content: (row) => row.currencyName ?? "-",
					minWidth: 92,
					name: "CURRENCY_NAME",
					sortFn: builtInCaseInsensitiveSortFor("currencyName"),
				}),

				BENCHMARK_NAME: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.BENCHMARK_NAME`),
					content: (row) => row.benchmarkName,
					minWidth: 192,
					name: "BENCHMARK_NAME",
					sortFn: builtInCaseInsensitiveSortFor("benchmarkName"),
				}),

				UNIVERSE_NAME: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.UNIVERSE_NAME`),
					content: (row) => row.universeName,
					minWidth: 192,
					name: "UNIVERSE_NAME",
					sortFn: builtInCaseInsensitiveSortFor("universeName"),
				}),

				COMMENTARY_TEMPLATE: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.COMMENTARY_TEMPLATE`),
					content: (row) => row.commentaryTemplate ?? "",
					minWidth: 192,
					name: "COMMENTARY_TEMPLATE",
					sortFn: builtInCaseInsensitiveSortFor("commentaryTemplate"),
				}),

				// benchmark eff ratio
				BENCHMARK_EFFICIENCY_RATIO_1M: (_params?: unknown) => ({
					...commonColumns.float("benchmarkEfficiencyRatio1M"),
					header: t(`TABLE.HEADERS.BENCHMARK_EFFICIENCY_RATIO_1M`),
					name: "EFFICIENCY_RATIO_1M",
					minWidth: 128,
				}),

				// benchmark max drawdown
				BENCHMARK_MAX_DRAWDOWN_1M: (_params?: unknown) => ({
					...commonColumns.percentage("benchmarkMaxDrawdown1M"),
					header: t(`TABLE.HEADERS.BENCHMARK_MAX_DRAWDOWN_1M`),
					name: "BENCHMARK_MAX_DRAWDOWN_1M",
					minWidth: 92,
				}),

				// benchmark performance
				BENCHMARK_PERFORMANCE_1M: (_params?: unknown) => ({
					...commonColumns.percentage("benchmarkPerformance1M"),
					header: t(`TABLE.HEADERS.BENCHMARK_PERFORMANCE_1M`),
					name: "BENCHMARK_PERFORMANCE_1M",
					minWidth: 128,
				}),

				// benchmark sortino
				BENCHMARK_SORTINO_1M: (_params?: unknown) => ({
					...commonColumns.float("benchmarkSortino1M"),
					header: t(`TABLE.HEADERS.BENCHMARK_SORTINO_1M`),
					name: "SORTINO_1M",
					minWidth: 128,
				}),

				//benchmark volatility
				BENCHMARK_VOLATILITY_1M: (_params?: unknown) => ({
					...commonColumns.percentage("benchmarkVolatility1M"),
					header: t(`TABLE.HEADERS.BENCHMARK_VOLATILITY_1M`),
					name: "BENCHMARK_VOLATILITY_1M",
					minWidth: 92,
				}),

				//efficiency ratio volatility
				EFFICIENCY_RATIO_1M: (_params?: unknown) => ({
					...commonColumns.float("efficiencyRatio1M"),
					header: t(`TABLE.HEADERS.EFFICIENCY_RATIO_1M`),
					name: "EFFICIENCY_RATIO_1M",
					minWidth: 92,
				}),

				// max drawdown
				MAX_DRAWDOWN_1M: (_params?: unknown) => ({
					...commonColumns.percentage("maxDrawdown1M"),
					header: t(`TABLE.HEADERS.MAX_DRAWDOWN_1M`),
					name: "MAX_DRAWDOWN_1M",
					minWidth: 128,
				}),

				// performance
				PERFORMANCE_1M: (_params?: unknown) => ({
					...commonColumns.percentage("performance1M"),
					header: t(`TABLE.HEADERS.PERFORMANCE_1M`),
					name: "PERFORMANCE_1M",
					minWidth: 128,
				}),

				// Sortino 1Y
				SORTINO_1M: (_params?: unknown) => ({
					...commonColumns.float("sortino1M"),
					header: t(`TABLE.HEADERS.SORTINO_1M`),
					name: "SORTINO_1M",
					minWidth: 64,
				}),

				// Volatility
				VOLATILITY_1M: (_params?: unknown) => ({
					...commonColumns.percentage("volatility1M"),
					header: t(`TABLE.HEADERS.VOLATILITY_1M`),
					sortFn: builtInSortFnFor("volatility1M"),
					name: "VOLATILITY_1M",
					minWidth: 92,
				}),

				// Portfolio Type (Portfolio Execution)
				PORTFOLIO_TYPE: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.PORTFOLIO_TYPE`),
					content: (row) =>
						row.liveTrading ? t(`PORTFOLIOS.PORTFOLIO_TYPE_LIVE`) : t(`PORTFOLIOS.PORTFOLIO_TYPE_PAPER`),
					// sortFn: builtInSortFnFor("liveTrading"),
					name: "LIVE_TRADING",
					minWidth: 96,
				}),
				WEIGHTING_METHOD: (_params?: unknown) => ({
					header: t(`TABLE.HEADERS.WEIGHTING_METHOD`),
					content: (row) => (row.fixedWeights ? "Fixed" : "Drifting"),
					// sortFn: builtInSortFnFor("fixedWeights"),
					name: "FIXED_WEIGHTS",
					minWidth: 96,
				}),
			}) satisfies Record<
				Exclude<UserPortfolioColumnPreferencePreferencesTypeEnum, "AVERAGE_SCORE" | "WARNINGS">,
				(...args: any[]) => TableColumn<ListInvestmentsDTO, FieldKey>
			>,
		[formatDate, user.id],
	);
}
